const DAY_MS = 86_400_000;
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

export function toFiniteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function todayIso(now = new Date()) {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function shiftIsoDate(date, days) {
  if (!date) return "";
  const base = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(base.getTime())) return "";
  return new Date(base.getTime() + days * DAY_MS).toISOString().slice(0, 10);
}

export function periodRange(days, referenceDate = todayIso()) {
  if (!days) return { from: "", to: "" };
  return { from: shiftIsoDate(referenceDate, -(days - 1)), to: referenceDate };
}

export function previousPeriodRange({ from, to }) {
  if (!isValidDateRange(from, to)) return { from: "", to: "" };
  const fromTime = new Date(`${from}T12:00:00Z`).getTime();
  const toTime = new Date(`${to}T12:00:00Z`).getTime();
  const inclusiveDays = Math.round((toTime - fromTime) / DAY_MS) + 1;
  const previousTo = shiftIsoDate(from, -1);
  return { from: shiftIsoDate(previousTo, -(inclusiveDays - 1)), to: previousTo };
}

export function isValidDateRange(from, to) {
  return Boolean(from && to && from <= to);
}

export function slotLabel(apps, slot, platformLabels = {}) {
  if (!slot?.appId) return "전체 앱";
  const app = apps.find(item => item.id === slot.appId);
  const appName = app?.app_name || `앱 ${slot.appId}`;
  if (!slot.platform || slot.platform === "all") return appName;
  return `${appName} · ${platformLabels[slot.platform] || slot.platform}`;
}

export function groupByDate(rows, valueKeys, dateKey = "date") {
  const grouped = new Map();
  for (const row of rows || []) {
    const date = row?.[dateKey];
    if (!date) continue;
    const current = grouped.get(date) || Object.fromEntries(valueKeys.map(key => [key, 0]));
    for (const key of valueKeys) current[key] += toFiniteNumber(row[key]);
    grouped.set(date, current);
  }
  return [...grouped.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, values]) => ({ [dateKey]: date, ...values }));
}

export function mergeSeriesByDate(rowsA, rowsB, valueKey, dateKey = "date") {
  const aggregate = rows => new Map(
    groupByDate(rows, [valueKey], dateKey).map(row => [row[dateKey], row[valueKey]])
  );
  const mapA = aggregate(rowsA);
  const mapB = aggregate(rowsB);
  const dates = [...new Set([...mapA.keys(), ...mapB.keys()])].sort();
  return dates.map(date => ({
    [dateKey]: date,
    valueA: mapA.get(date) ?? 0,
    valueB: mapB.get(date) ?? 0,
  }));
}

export function alignPeriods(dataA, dataB, dateKey, valueKey) {
  const sort = rows => [...(rows || [])].sort((a, b) => String(a[dateKey]).localeCompare(String(b[dateKey])));
  const a = sort(dataA);
  const b = sort(dataB);
  const length = Math.max(a.length, b.length);
  return Array.from({ length }, (_, index) => ({
    day: `D${index + 1}`,
    dateA: String(a[index]?.[dateKey] || "").slice(5),
    dateB: String(b[index]?.[dateKey] || "").slice(5),
    valueA: a[index]?.[valueKey] ?? null,
    valueB: b[index]?.[valueKey] ?? null,
  }));
}

export function sumMetric(rows, key) {
  return (rows || []).reduce((sum, row) => sum + toFiniteNumber(row?.[key]), 0);
}

export function averageMetric(rows, key) {
  const values = (rows || []).map(row => Number(row?.[key])).filter(Number.isFinite);
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
}

export function latestMetric(rows, key, dateKey = "date") {
  const sorted = [...(rows || [])].sort((a, b) => String(a?.[dateKey]).localeCompare(String(b?.[dateKey])));
  return toFiniteNumber(sorted.at(-1)?.[key]);
}

export function weightedAverage(rows, valueKey, weightKey) {
  const weight = sumMetric(rows, weightKey);
  if (!weight) return 0;
  return (rows || []).reduce(
    (sum, row) => sum + toFiniteNumber(row?.[valueKey]) * toFiniteNumber(row?.[weightKey]),
    0,
  ) / weight;
}

export function ecpmFromTotals(rows) {
  const impressions = sumMetric(rows, "impressions");
  return impressions ? (sumMetric(rows, "earnings") / impressions) * 1000 : 0;
}

export function formatDateWithDay(date) {
  if (!date) return "";
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return `${date.slice(5)} (${DAY_NAMES[parsed.getDay()]})`;
}

export function formatPercent(value, digits = 1) {
  return `${(toFiniteNumber(value) * 100).toFixed(digits)}%`;
}
